import type { useDrag } from "@use-gesture/react";
import React, { useState } from "react";

import { useClient } from "../../../../hooks/useClient";
import { useMessage } from "../../../../hooks/useMessage";
import { PhotoSelectionPhoto } from "./photo-selection-photo";

import "./photo-selection-photo.css";

// We will rerender when the result of this string changes
// Currently we only rerender if another type of volatileImageRef is used (Partner / Local / SmartPhoto / Rejected )
// Example: later we will also need to rerender if usage count is changed
function getRerenderKey(photo: eXpress.core.Photo) {
	return eXpress.utils.getConstructorName(photo.volatileImageRef);
}

/** This container will rerender it child component if its state is changed (downloaded/uploaded) */
export function PhotoSelectionPhotoContainer({
	// Should we use memo to prevent rerenders?
	className,
	photo,
	onClick,
	...gestureHandlers
}: {
	className?: string;
	photo: eXpress.core.Photo;
	onClick?: () => void;
	// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
} & ReturnType<ReturnType<typeof useDrag>>) {
	// We don't rerender on each photo state change (see useMessage)
	// generating an URL can generate an unique URL each time (to prevent caching)
	// To prevent rerendering with a new image url at each refresh, we use only rerender if following string changes
	const [rerenderKey, setRerenderKey] = useState(() => getRerenderKey(photo));

	const client = useClient();

	// Refresh when photostate is changed
	useMessage((message) => {
		// No useCallback because I don't think the performance will benefit much here
		if (eXpress.core.isPhotoStateChanged(message, photo)) {
			const newRerenderKey = getRerenderKey(photo);
			if (rerenderKey !== newRerenderKey) {
				setRerenderKey(newRerenderKey);
			}
		}
	});

	return (
		<PhotoSelectionPhoto
			item={{
				// PhotoReference ID (TODO: make a PhotoReferenceString type for it?)
				id: photo.id,
				// eslint-disable-next-line @typescript-eslint/no-unsafe-call
				url: photo.orientedMediumUrl?.getAbsoluteUrl(client) || "",
				width: photo.width,
				height: photo.height,
				title: photo.fileName,
				isRejected: photo.state === eXpress.core.PhotoState.Rejected,
				angle: photo.orientedAngle,
			}}
			className={`photo-selection-photo ${className || ""}`}
			{...gestureHandlers}
			onClick={onClick}
		/>
	);
}
